import type { Module } from "@/types/config-page";

export type ConfigPageAuthMode = "guest" | "authenticated";

export const GUEST_HIDDEN_MODULE_TYPES = new Set([
  "busi-raffle",
  "busi-ranking",
  "buyMemberGoods",
  "buyMemberCard",
  "customizeBuyMember",
  "myMemberGoods",
  "memberBooking",
]);

/**
 * 分享助力链接：query 中同时带 shareUid（分享人 uid）与 shareType（分享场景，如 ranking）。
 *
 * 这类链接的全部功能都依赖登录态：助力上报 userShareTask、加入队伍 joinTheTeam 都要当前
 * 用户 uid，而承载这些交互的排行榜组件在游客态会被 GUEST_HIDDEN_MODULE_TYPES 直接裁掉；
 * 更麻烦的是 ConfigPageProvider 挂载瞬间的 openedAsGuest 快照会永久抑制助力上报
 * （见 configPage/page.tsx 的 shareUserOpen）。也就是说游客态打开分享助力链接，看到的是
 * 一个没有任何可用内容、且助力静默失效的页面。
 *
 * 因此不论后台把 loginRequired 配成什么，分享助力链接一律先要求登录。
 */
export const isShareAssistLink = ({
  shareUid,
  shareType,
}: {
  shareUid?: string;
  shareType?: string;
}): boolean => Boolean(shareUid && shareType);

const getModuleJsonData = (module: Module) => {
  if (module.moduleJsonData) {
    return module.moduleJsonData;
  }
  try {
    return JSON.parse(module.moduleJson || "{}");
  } catch (error) {
    console.warn("专题页组件配置解析失败，匿名态下不展示该组件", error);
    return null;
  }
};

export const filterGuestModules = (moduleList: Module[] = []): Module[] => {
  return moduleList.filter((module) => {
    const moduleJsonData = getModuleJsonData(module);
    if (!moduleJsonData) {
      return false;
    }
    const moduleType = module.moduleType || moduleJsonData.type;
    const hasCrowdCondition = Boolean(moduleJsonData?.crowd?.checkAll?.length);
    return !hasCrowdCondition && !GUEST_HIDDEN_MODULE_TYPES.has(moduleType);
  });
};

type GuestStairsNode = Record<string, unknown> & {
  canClaim?: boolean;
};

type GuestStairsStatus = Record<string, unknown> & {
  cumulateAmt?: number;
  nodeList?: GuestStairsNode[];
};

export const normalizeGuestStairsStatusMap = (
  resultMap: Record<string, GuestStairsStatus> = {},
): Record<string, GuestStairsStatus> => {
  return Object.fromEntries(
    Object.entries(resultMap).map(([activityCode, status]) => [
      activityCode,
      {
        ...status,
        cumulateAmt: 0,
        nodeList: (status?.nodeList || []).map((node) => ({
          ...node,
          canClaim: false,
        })),
      },
    ]),
  );
};
